import { MaterialService } from './material.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Lilate, LilateList } from '../model/lilate.model';
import { CandidatePath, CandidatePathProductRef } from '../model/candidate-path.model';
import { AIProvidersIncidentsResponse } from '../model/ai.model';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { Router } from '@angular/router';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { assessmentResult, lilateGrid, lilateGridCriteria, lilateGridCriteriaList, lilateGridList, lilateResultsCriteriaList } from '../model/assessment.model';
import moment from 'moment';
import { random } from 'lodash';
import { TeacherList } from '../model/teacher.model';
import { NotificationList } from '../model/notification.model';
import { ServiceValidity } from '../model/service-validity.model';
import { lilateExamDate } from '../utils/lilate-exam-date.util';

@Injectable({
    providedIn: 'root'
})
export class LilateService extends KiwixiService {
    apiLilateUrl = environment.server + 'lilate_exams/';
    apiLilatBulkeUrl = environment.server + 'lilate_exams_bulk/';
    apiLilateCertificateUrl = environment.server + 'lilate_certificate/';
    apiLilateCertificateDownload = environment.server + 'lilate_certificate/generatepdf/';
    apiLilateLevelAttestationDownload = environment.server + 'lilate_level_attestation/generatepdf/';
    apiLilateRealizationCertificateDownload = environment.server + 'lilate_realization_certificate/generatepdf/';
    apiLilateResultUrl = environment.server + 'lilate_results/';
    apiLilateResultCriteriaUrl = environment.server + 'lilate_results_criteria/';
    apiLilatePlanUrl = environment.server + 'plan/';
    apiLilategridUrl = environment.server + 'lilate_grids/';
    apiLilategridCriteraUrl = environment.server + 'lilate_grids_criteria/';

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _http: HttpClient,
        private _router: Router,
        kiwixiGlobals: KiwixiGlobals,
        private materialService: MaterialService,) {
        super(_http, _router, kiwixiGlobals, materialService);
    }

    get(params: any, bulk=null): Observable<LilateList> {
        const header = this.createHttpClientAuthorizationHeader();
        const url = bulk ? this.apiLilatBulkeUrl : this.apiLilateUrl;
        return this._httpClient.get<LilateList>(url, {
            params: params,
            headers: header
        });
    }

    getFromUrl(url: string): Observable<LilateList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._httpClient.get<LilateList>(url, {
            headers: header
        });
    }

    getOne(lilateId: any, salt = false): Observable<Lilate> {
        const header = this.createHttpClientAuthorizationHeader();
        let url = this.apiLilateUrl + lilateId + '/';
        if (salt)
            url += '?salt=' + Math.random();
        return this._httpClient.get<Lilate>(url, {
            headers: header
        }).pipe(map((lilate) => this.normalizeLilatePath(lilate)));
    }

    /**
     * Incidents fournisseurs IA durant le test (pour LILATE modalité IA).
     * GET /api/v1/ai-providers-incidents/?lilate_id=<id>
     */
    getAiProvidersIncidents(lilateId: number): Observable<AIProvidersIncidentsResponse> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._httpClient.get<AIProvidersIncidentsResponse>(
            environment.server + 'ai-providers-incidents/?lilate_id=' + lilateId,
            { headers: header }
        );
    }

    post(data): Observable<Lilate> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._httpClient.post<Lilate>(this.apiLilateUrl, data);
    }

    patch(data, lilateId): Observable<Lilate> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._httpClient.patch<Lilate>(this.apiLilateUrl + lilateId + '/', data, { headers: header })
            .pipe(map((lilate) => this.normalizeLilatePath(lilate)));
    }

    plan(data): Observable<Lilate> {
        return this._httpClient.post<Lilate>(this.apiLilatePlanUrl, data);
    }

    /**
     * Fenêtre de validité du LILATE et droits d'action (staff).
     * GET /lilate_exams/<id>/validity/
     */
    getValidity(lilateId: number): Observable<ServiceValidity> {
        return this._httpClient.get<ServiceValidity>(`${this.apiLilateUrl}${lilateId}/validity/`);
    }

    /**
     * Prolonge la date butoir : action unique, nouvelle date calculée par Engage.
     * POST /lilate_exams/<id>/extend-validity/
     */
    extendValidity(lilateId: number): Observable<ServiceValidity> {
        return this._httpClient.post<ServiceValidity>(`${this.apiLilateUrl}${lilateId}/extend-validity/`, {});
    }

    /**
     * Réactive un LILATE expiré : action unique, nouvelle date calculée par Engage.
     * POST /lilate_exams/<id>/reactivate/
     */
    reactivate(lilateId: number): Observable<ServiceValidity> {
        return this._httpClient.post<ServiceValidity>(`${this.apiLilateUrl}${lilateId}/reactivate/`, {});
    }

    getGrid(param): Observable<lilateGridList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateGridList>(this.apiLilategridUrl + '?parent__isnull=True', { headers: header, params: param });
    }

    getAllGrid(param): Observable<lilateGridList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateGridList>(this.apiLilategridUrl, { headers: header, params: param });
    }

    getSkills(param): Observable<lilateGridCriteriaList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateGridCriteriaList>(this.apiLilategridCriteraUrl, { headers: header, params: param })
    }

    getLilatesUpcomingsAssessments(params?: HttpParams): Observable<LilateList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<LilateList>(this.apiLilateUrl + 'upcomings_assessments/', { headers: header, params: params });
    }

    getLilatesPendingAssessments(params?: HttpParams): Observable<LilateList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<LilateList>(this.apiLilateUrl + 'pending_assessments/', { headers: header, params: params })
    }

    getLilatesPast(params?: HttpParams): Observable<LilateList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<LilateList>(this.apiLilateUrl + 'past/', { headers: header, params: params }).pipe(
            map((list) => this.normalizeLilateListPaths(list)),
        );
    }

    getLilatesUpcomings(params?: HttpParams): Observable<LilateList> {
        // createOptionalAuthHeader et non createHttpClientAuthorizationHeader :
        // celui-ci déconnecte sans bundle, avant même l'émission de la requête.
        // L'en-tête est posé ici et pas seulement par l'AuthInterceptor, que
        // l'espace enseignant n'enregistre pas (la requête partait sans jeton
        // et répondait 403 : exploration e2e du 01/11/2026).
        return this._http.get<LilateList>(this.apiLilateUrl + 'upcomings/', {
            headers: this.createOptionalAuthHeader(),
            params: params,
        }).pipe(
            map((list) => this.normalizeLilateListPaths(list)),
        );
    }

    /**
     * Normalise les données parcours / FlashLevel imbriquées renvoyées par l'API upcomings et past.
     */
    private normalizeLilateListPaths(list: LilateList): LilateList {
        if (!list?.results?.length) {
            return list;
        }
        return {
            ...list,
            results: list.results.map((lilate) => this.normalizeLilatePath(lilate)),
        };
    }

    private normalizeLilatePath(lilate: Lilate): Lilate {
        if (!lilate?.path) {
            return lilate;
        }
        return {
            ...lilate,
            path: this.normalizeCandidatePath(lilate.path),
        };
    }

    private normalizeCandidatePath(path: CandidatePath): CandidatePath {
        // Conserver levelup ; bundle = legacy ES non réindexé.
        let pathType: CandidatePath['path_type'] = 'standalone';
        if (path.path_type === 'levelup' || path.path_type === 'bundle') {
            pathType = path.path_type;
        }
        const progression = path.progression as CandidatePath['progression'];
        return {
            id: path.id,
            path_type: pathType,
            partners_path_id: path.partners_path_id,
            status: path.status,
            can_start_lilate: typeof path.can_start_lilate === 'boolean' ? path.can_start_lilate : undefined,
            can_start_flashlevel: typeof path.can_start_flashlevel === 'boolean' ? path.can_start_flashlevel : undefined,
            entry_level: (path.entry_level as string) || null,
            exit_level: (path.exit_level as string) || null,
            progression: progression ? { done: progression.done, total: progression.total } : null,
            flashlevel: path.flashlevel ? this.normalizePathProductRef(path.flashlevel) : null,
            lilate: path.lilate ? this.normalizePathProductRef(path.lilate) : null,
        };
    }

    private normalizePathProductRef(ref: CandidatePathProductRef): CandidatePathProductRef {
        return {
            id: ref.id,
            status: ref.status,
            level: ref.level,
            token: ref.token,
        };
    }

    getLilateIdentity(lilateId: number) {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get(this.apiLilateUrl + 'identity_card/' + lilateId + '/', { headers: header, responseType: 'blob' });
    }

    postResult(data): Observable<assessmentResult> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.post<assessmentResult>(this.apiLilateResultUrl + 'save_assessment/', data, { headers: header })
    }

    getLilateParchment(token: string): Observable<any> {
        return this._http.get<any>(this.apiLilateCertificateUrl + 'data/' + token);
    }

    getLilateResultCriteria(params?: HttpParams): Observable<lilateResultsCriteriaList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateResultsCriteriaList>(this.apiLilateResultCriteriaUrl, { headers: header, params: params });
    }

    getGridCriteriaByUrl(url): Observable<lilateGridCriteria> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateGridCriteria>(url, { headers: header });
    }

    getGridByUrl(url): Observable<lilateGrid> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<lilateGrid>(url, { headers: header });
    }

    downloadPdf(lilate: Lilate, skills = false) {
        this._http.get(this.apiLilateCertificateDownload + lilate.token_certificate + '?skills=' + skills, { responseType: 'arraybuffer' })
            .subscribe(
                {
                    next: (file: ArrayBuffer) => {
                        const _date = moment(lilateExamDate(lilate)).format('DD_MM_YYYY');
                        const _name = '_' + lilate.student.user.first_name + '_' + lilate.student.user.last_name;
                        const filename = 'certificat_' + _date + _name;
                        this.downloadFile(filename, file, 'application/pdf');
                    },
                    error: (err: HttpErrorResponse) => {
                        if (err.status === 400)
                            this.materialService.openSnackBar('Photo non validée ou introuvable', 'red')
                    }
                }
            )
    }

    downloadLilateLevelAttestationPdf(lilate: Lilate) {
        this._http.get(this.apiLilateLevelAttestationDownload + lilate.token_certificate, { responseType: 'arraybuffer' })
            .subscribe((file: ArrayBuffer) => {
                const _date = moment(lilateExamDate(lilate)).format('DD_MM_YYYY');
                const _name = '_' + lilate.student.user.first_name + '_' + lilate.student.user.last_name;
                const filename = 'attestation_niveau_' + _date + _name;
                this.downloadFile(filename, file, 'application/pdf');
            });
    }

    downloadLilateRealizationCertificatePdf(lilate: Lilate) {
        this._http.get(this.apiLilateRealizationCertificateDownload + lilate.token_certificate, { responseType: 'arraybuffer' })
            .subscribe((file: ArrayBuffer) => {
                const _date = moment(lilateExamDate(lilate)).format('DD_MM_YYYY');
                const _name = '_' + lilate.student.user.first_name + '_' + lilate.student.user.last_name;
                const filename = 'certificat_réalisation_' + _date + _name;
                this.downloadFile(filename, file, 'application/pdf');
            });
    }

    getObservableAvailableTeachers(id: number, params?: HttpParams): Observable<TeacherList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<TeacherList>(this.apiLilateUrl + id + '/available-teachers/', {
            headers: header,
            params: params
        })
            .pipe(
                catchError(this.handleErrorObservable<TeacherList>(`getObservableTeachers`))
            );
    }

    getLilateNotifications(lilateId: number, pageSize: number = 25): Observable<NotificationList> {
        const header = this.createHttpClientAuthorizationHeader();
        const params = new HttpParams().set('page_size', pageSize.toString());
        return this._http.get<NotificationList>(this.apiLilateUrl + lilateId + '/notifications/', { headers: header, params });
    }

    getLilateNotificationsWithUrl(url: string): Observable<NotificationList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<NotificationList>(url, { headers: header });
    }

    getLilateNotificationById(lilateId: number, notificationId: number): Observable<NotificationList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<NotificationList>(`${this.apiLilateUrl}${lilateId}/notifications/${notificationId}/`, { headers: header });
    }

    getCancelStatus() {
        return [
            { code: 'duplicate', label: 'Doublon', valid: true },
            { code: 'refuse', label: "Refuse de passer l'examen", valid: true },
            { code: 'reschedule', label: 'Aide à la replanification', valid: true },
            { code: 'other', label: 'Autre', valid: true },
        ]
    }

    getStatus() {
        return [
            { label: 'En attente de planification', code: 'pending_booking', valid: true },
            { label: 'Planifié', code: 'booked', valid: true },
            { label: 'Réalisé', code: 'done', valid: true },
            { label: 'Annulé par l\'élève', code: 'cancelled_by_student', valid: true },
            { label: 'Annulé par un administrateur', code: 'cancelled_by_admin', valid: true },
            { label: 'Annulé par le professeur', code: 'cancelled_by_teacher', valid: true },
            { label: 'Suspendue', code: 'suspended', valid: true },
            { label: 'Problème concernant l\'idendité de l\'élève', code: 'identity_issue', valid: true },
            { label: 'Teacher Absent', code: 'abs_teacher_without_justification', valid: true },
            { label: 'Student Absent', code: 'abs_student_without_justification', valid: true },
            { label: 'Expiré', code: 'expired', valid: true },
            { label: 'Abandonné', code: 'abandoned', valid: true },
            // Statuts IA
            { label: 'En attente (IA)', code: 'pending', valid: true },
            { label: 'En cours (IA)', code: 'ongoing', valid: true },
        ];
    }

    getModalityOptions() {
        return [
            { label: 'Examinateur', code: 'examiner' },
            { label: 'IA', code: 'ai' },
        ];
    }

    exportLilateLogs(lilateId: number): Observable<Blob> {
        const headers = this.createHttpClientAuthorizationHeader();
        const url = `${environment.server}lilate_exams/${lilateId}/ai-engine-logs-export/`;
        return this._http.get(url, { headers, responseType: 'blob' }).pipe(
            catchError(this.handleErrorObservable<Blob>('exportLilateLogs'))
        );
    }

    getStatusEs() {
        return [
            { label: 'En attente', valid: true, color: '#FF7F50' },
            { label: 'Planifié', valid: true, color: '#00BFFF' },
            { label: 'Réalisé', valid: true, color: '#32CD32' },
            { label: 'Annulé', valid: true, color: '#FF4500' },
            { label: 'Suspendu', valid: true, color: '#FF69B4' },
            { label: 'Absence', valid: true, color: '#FFD700' },
            { label: 'Expiré', valid: true, color: '#808080' },
            { label: 'Abandonné', valid: true, color: '#696969' }
        ];
    }

    getModeFinancement() {
        return [
            { label: "CPF", code: "cpf", valid: true, color: '#FF5733' },
            { label: "Co Financement", code: "co_financement", valid: true, color: '#33FF57' },
            { label: "Autre", code: "autres", valid: true, color: '#808080' },
            { label: "Plan de développement et de compétences - OPCO", code: "opco", valid: true, color: '#FFFF33' },
            { label: "Service public (on forme les gens de l'Etat)", code: "service_public", valid: true, color: '#FF33FF' },
            { label: "Pôle Emploi", code: "pole_emploi", valid: true, color: '#33FFFF' },
            { label: "Financement personnel", code: "financement_personnel", valid: true, color: '#000000' },
            { label: "Sous-traitance", code: "sous_traitance", valid: true, color: '#FFA500' },
            { label: "FNE", code: "fne", valid: true, color: '#800080' },
            { label: "Entreprise", code: "entreprise", valid: true, color: '#3357FF' },
        ];
    }

    getPathEventType() {
        return [
            { label: "LILATE créé", code: "lilate_available", valid: true },
            { label: "LILATE planifié", code: "lilate_booked", valid: true },
            { label: "LILATE reporté", code: "lilate_rescheduled", valid: true },
            { label: "Résultat LILATE disponible", code: "lilate_done", valid: true },
            { label: "Lilate annulé par un administrateur", code: "lilate_cancelled_by_admin", valid: true },
            { label: "Lilate annulé par l'élève", code: "lilate_cancelled_by_student", valid: true },
            { label: "LILATE annulé par examinateur", code: "lilate_cancelled_by_teacher", valid: true },
            { label: "Absence candidat", code: "lilate_student_absent", valid: true },
            { label: "Absence examinateur", code: "lilate_teacher_absent", valid: true },
            { label: "Photo non conforme", code: "lilate_photo_student_refused", valid: true },
        ]
    }

    getLevelsChoices() {
        return [
            { label: "Débutant", code: "A1", valid: true },
            { label: "Intérmediaire", code: "B1", valid: true },
            { label: "Avancé", code: "C1", valid: true },
        ]
    }

    getJobSituationOptions() {
        return [
            { label: "Je suis étudiant", code: "student", valid: true, color: "#FF5733" },
            { label: "Je suis à la recherche d'un emploi", code: "unemployed", valid: true, color: "#33FF57" },
            { label: "Je suis en poste", code: "employed", valid: true, color: "#3357FF" },
            { label: "Je suis à la retraite", code: "retired", valid: true, color: "#FFFF33" },
            { label: "NC", code: "NC", valid: true, color: "#808080" },
        ];
    }

    getGenders() {
        return [
            { label: "Homme", code: "male", valid: true, color: "#3498db" },
            { label: "Femme", code: "female", valid: true, color: "#e74c3c" },
            { label: "NC", code: "NC", valid: true, color: "#808080" },
        ];
    }

    getAgeRange() {
        return [
            { label: "Moins de 18 ans", code: "Moins de 18 ans", valid: true, color: "#319795" },
            { label: "18-24 ans", code: "18-24 ans", valid: true, color: "#4FD1C5" },
            { label: "25-34 ans", code: "25-34 ans", valid: true, color: "#C0E600" },
            { label: "35-44 ans", code: "35-44 ans", valid: true, color: "#3E7AF8" },
            { label: "45-54 ans", code: "45-54 ans", valid: true, color: "#F2B807" },
            { label: "55-64 ans", code: "55-64 ans", valid: true, color: "#D926A9" },
            { label: "65 ans et plus", code: "65 ans et plus", valid: true, color: "#79D930" },
            { label: "NC", code: "NC", valid: true, color: "#808080" },
        ];
    }

    getMinimumAccesTime() {
        return [
            { label: "Le jour même", code: 0, valid: true },
            { label: "Le lendemain", code: 1, valid: true },
        ];
    }

    getLilateInteruptReasons() {
        return [
            { code: "student_absent", label: "Le candidat ne s'est pas présenté" },
            { code: "student_leave", label: "Le candidat a quitté la session sans explication" },
            { code: "student_refuse", label: "Le candidat a refusé de passer l’examen" },
            { code: "device_issue", label: "Le candidat a un problème de caméra ou de micro" },
            { code: "device_network", label: "Le candidat a un problème d'internet" },
            { code: "other", label: "Autre raison" }
        ];
    }

    level_to_numeric(level) {
        if (level == 'C2')
            return 6
        else if (level == 'C1')
            return 5
        else if (level == 'B2')
            return 4
        else if (level == 'B1')
            return 3
        else if (level == 'A2')
            return 2
        else if (level == 'A1')
            return 1
        else if (level == 'A0')
            return 0
        else
            return null
    }

    checkIdentidyValidity(lilate: Lilate) {
        // LILATE IA: Vérification Stripe Identity
        if (lilate.modality === 'ai') {
            return lilate.identity_verification?.status === 'verified';
        }
        
        // LILATE Classique: Vérification photo profile
        if (!lilate.student.user.photo_profile) {
            return false;
        }
        else { // si profile
            // et cela a été validé
            if (lilate.student.user.photo_profile.status == 'validated') {
                return true;
            } else {
                return false;
            }
        }
    }
    
    // === MÉTHODES MODALITÉ IA ===
    
    /**
     * Récupère un LILATE par son ai_token (modalité IA uniquement)
     * Source: api-recurring/recurring/lilate/documentation/specs/lilate_ai.md#9
     */
    getObservableLilateByToken(token: string): Observable<Lilate> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.get<Lilate>(
            `${environment.server}lilates_token/${token}/`,
            { headers }
        ).pipe(
            catchError(this.handleErrorObservable<Lilate>('getObservableLilateByToken'))
        );
    }
    
    /**
     * Démarre un test LILATE IA (passage pending → ongoing)
     * Crée le test plan sur Rakam via l'endpoint backend
     * 
     * Source: api-recurring/recurring/lilate/documentation/specs/lilate_ai.md#9
     * 
     * Note: Retourne ai_test_plan_id et ai_token Rakam (usage interne backend)
     * Pour naviguer côté frontend, utiliser lilate.token_certificate
     * 
     * @param lilateId - ID du LILATE
     * @returns Observable avec ai_test_plan_id et ai_token (Rakam)
     */
    startAiTest(lilateId: number): Observable<{ ai_test_plan_id: number; ai_token: string }> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._httpClient.post<{ ai_test_plan_id: number; ai_token: string }>(
            `${this.apiLilateUrl}${lilateId}/start_ai_test/`,
            {},
            { headers }
        ).pipe(
            catchError(this.handleErrorObservable<{ ai_test_plan_id: number; ai_token: string }>('startAiTest'))
        );
    }
    
    /**
     * Récupère les logs détaillés du test IA Rakam (modalité IA uniquement)
     * Source: api-recurring/recurring/lilate/documentation/specs/lilate_ai.md#9
     */
    getAiEngineLogs(lilateId: number): Observable<any> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.get<any>(
            `${this.apiLilateUrl}${lilateId}/ai-engine-logs/`,
            { headers }
        ).pipe(
            catchError(this.handleErrorObservable<any>('getAiEngineLogs'))
        );
    }
    
    /**
     * Export XLS des exercices du test IA
     * Source: api-recurring/recurring/lilate/documentation/specs/lilate_ai.md#9
     */
    getAiEngineLogsExport(lilateId: number): Observable<Blob> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.get(
            `${this.apiLilateUrl}${lilateId}/ai-engine-logs-export/`,
            { headers, responseType: 'blob' }
        ).pipe(
            catchError(this.handleErrorObservable<Blob>('getAiEngineLogsExport'))
        );
    }

    /**
     * Génère le prompt contextuel pour un LILATE IA.
     * POST /lilate_exams/<id>/generate-prompt-context/
     *
     * Détermine automatiquement le type (GENERIC / CUSTOM) et génère le contenu.
     * Si company_name + website sont fournis, utilise OpenAI côté backend.
     *
     * @param lilateId - ID du LILATE
     * @param data - Données métier/entreprise
     * @returns Observable avec ai_prompt_type, ai_prompt_context et champs mis à jour
     */
    generatePromptContext(
        lilateId: number,
        data: {
            ai_job?: string | null;
            ai_business?: string | null;
            ai_company_name?: string | null;
            ai_website?: string | null;
        }
    ): Observable<{
        ai_prompt_type: 'GENERIC' | 'CUSTOM';
        ai_prompt_context: string;
        ai_job: string | null;
        ai_business: string | null;
        ai_company_name: string | null;
        ai_website: string | null;
    }> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._httpClient.post<{
            ai_prompt_type: 'GENERIC' | 'CUSTOM';
            ai_prompt_context: string;
            ai_job: string | null;
            ai_business: string | null;
            ai_company_name: string | null;
            ai_website: string | null;
        }>(
            `${this.apiLilateUrl}${lilateId}/generate-prompt-context/`,
            data,
            { headers }
        ).pipe(
            catchError(this.handleErrorObservable<{
                ai_prompt_type: 'GENERIC' | 'CUSTOM';
                ai_prompt_context: string;
                ai_job: string | null;
                ai_business: string | null;
                ai_company_name: string | null;
                ai_website: string | null;
            }>('generatePromptContext'))
        );
    }

    /**
     * Transfère un LILATE vers un autre candidat (staff uniquement).
     * POST /lilate_exams/<id>/transfer/
     *
     * @param lilateId - ID du LILATE à transférer
     * @param payload  - { mode: 'existing_user', user_id } ou { mode: 'new_user', user: {...} }
     * @returns Observable avec le LILATE mis à jour (sérialiseur complet)
     */
    transferLilate(lilateId: number, payload: TransferLilatePayload): Observable<Lilate> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._httpClient.post<Lilate>(
            `${this.apiLilateUrl}${lilateId}/transfer/`,
            payload,
            { headers }
        ).pipe(
            catchError(this.handleErrorObservable<Lilate>('transferLilate'))
        );
    }
}

export interface TransferLilateNewUserPayload {
    gender?: string;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    mobile_phone?: string;
    birthdate?: string;
    /** Code de la langue principale (ex. 'language.fr'). Défaut serveur : 'language.fr'. */
    main_language?: string;
    /** Identifiant IANA du fuseau horaire (ex. 'Europe/Paris'). Défaut serveur : 'Europe/Paris'. */
    timezone?: string;
}

export type TransferLilatePayload =
    | { mode: 'existing_user'; user_id: number }
    | { mode: 'new_user'; user: TransferLilateNewUserPayload };
